import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, AlertCircle, Flag } from 'lucide-react';

const SOXFlowcharts = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [riskFilter, setRiskFilter] = useState('All Risk Levels');
  const [cycles, setCycles] = useState([]);
  const [filteredCycles, setFilteredCycles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  // Sample data matching the images
  const soxCycles = [
    {
      id: 1,
      name: 'Order to Cash',
      description: 'Revenue recognition, accounts receivable, and cash collection processes',
      riskLevel: 'High Risk',
      riskColor: 'bg-red-100 text-red-700 border-red-200',
      processes: [
        { name: 'Sales Order Processing', status: 'current', controls: 8, lastReviewed: '2024-11-15', hasFlowchart: true },
        { name: 'Revenue Recognition', status: 'needs review', controls: 12, lastReviewed: '2024-10-01', hasFlowchart: true, hasFlag: true },
        { name: 'Customer Credit Management', status: 'current', controls: 0, lastReviewed: '2024-11-20', hasFlowchart: false }
      ],
      processCount: 3,
      lastUpdated: '2024-12-01'
    },
    {
      id: 2,
      name: 'Procure to Pay',
      description: 'Procurement, accounts payable, and vendor payment processes',
      riskLevel: 'Moderate Risk',
      riskColor: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      processes: [
        { name: 'Purchase Requisition', status: 'current', controls: 6, lastReviewed: '2024-11-10', hasFlowchart: true },
        { name: 'Vendor Invoice Processing', status: 'outdated', controls: 10, lastReviewed: '2024-09-15', hasFlowchart: true, hasFlag: true }
      ],
      processCount: 2,
      lastUpdated: '2024-11-28'
    },
    {
      id: 3,
      name: 'Hire to Retire',
      description: 'Payroll, benefits, and human resources processes',
      riskLevel: 'Moderate Risk',
      riskColor: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      processes: [
        { name: 'Payroll Processing', status: 'current', controls: 9, lastReviewed: '2024-11-05', hasFlowchart: true },
        { name: 'Employee Onboarding', status: 'current', controls: 0, lastReviewed: '2024-10-30', hasFlowchart: false }
      ],
      processCount: 2,
      lastUpdated: '2024-11-25'
    },
    {
      id: 4,
      name: 'Record to Report',
      description: 'General ledger, financial close, and reporting processes',
      riskLevel: 'High Risk',
      riskColor: 'bg-red-100 text-red-700 border-red-200',
      processes: [
        { name: 'Month-End Close', status: 'needs review', controls: 15, lastReviewed: '2024-10-15', hasFlowchart: true, hasFlag: true },
        { name: 'Journal Entry Processing', status: 'current', controls: 7, lastReviewed: '2024-11-18', hasFlowchart: true }
      ],
      processCount: 2,
      lastUpdated: '2024-12-02'
    }
  ];

  useEffect(() => {
    // Simulate loading animation on mount
    setTimeout(() => {
      setCycles(soxCycles);
      setFilteredCycles(soxCycles);
      setIsLoading(false);
    },  );
  }, []);

  useEffect(() => {
    // Add search animation delay
    setIsSearching(true);
    const searchTimer = setTimeout(() => {
      let filtered = [...cycles];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.map(cycle => {
        const matchingProcesses = cycle.processes.filter(process =>
          process.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        
        if (cycle.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
            cycle.description.toLowerCase().includes(searchQuery.toLowerCase())) {
          return cycle;
        } else if (matchingProcesses.length > 0) {
          return { ...cycle, processes: matchingProcesses };
        }
        return null;
      }).filter(Boolean);
    }

    // Apply status filter
    if (statusFilter !== 'All Statuses') {
      filtered = filtered.map(cycle => {
        const matchingProcesses = cycle.processes.filter(process => {
          if (statusFilter === 'Current') return process.status === 'current';
          if (statusFilter === 'Needs Review') return process.status === 'needs review';
          if (statusFilter === 'Outdated') return process.status === 'outdated';
          return true;
        });
        
        if (matchingProcesses.length > 0) {
          return { ...cycle, processes: matchingProcesses };
        }
        return null;
      }).filter(Boolean);
    }

    // Apply risk filter
    if (riskFilter !== 'All Risk Levels') {
      filtered = filtered.filter(cycle => cycle.riskLevel === riskFilter);
    }

    setFilteredCycles(filtered);
    setIsSearching(false);
    }, 200);

    return () => clearTimeout(searchTimer);
  }, [searchQuery, statusFilter, riskFilter, cycles]);

  const getStatusStyles = (status) => {
    switch (status) {
      case 'current':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'needs review':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'outdated':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const ProcessCard = ({ process }) => (
    <div className="flex items-center justify-between py-4 px-6 hover:bg-gray-50 hover:pl-8 transition-all duration-200 ease-out">
      <div className="flex items-center space-x-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-110 ${
          process.status === 'current' ? 'bg-green-100' : 
          process.status === 'needs review' ? 'bg-yellow-100' : 'bg-red-100'
        }`}>
          <svg className={`w-5 h-5 ${
            process.status === 'current' ? 'text-green-600' : 
            process.status === 'needs review' ? 'text-yellow-600' : 'text-red-600'
          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-gray-900 font-medium">{process.name}</span>
          {process.hasFlag && (
            <Flag className="w-4 h-4 text-red-500 animate-pulse" fill="currentColor" />
          )}
        </div>
      </div>
      
      <div className="flex items-center space-x-6">
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusStyles(process.status)}`}>
          {process.status}
        </span>
        <div className="text-sm text-gray-600">
          {process.controls > 0 ? `${process.controls} controls` : 
           process.hasFlowchart ? 'No flowchart' : 'No flowchart'}
        </div>
        <div className="text-sm text-gray-500">
          Last reviewed: {process.lastReviewed}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">
            SOX Flowcharts
          </h1>
          <p className="text-gray-600 mt-2">
            Manage and review SOX cycle processes and their associated flowcharts
          </p>
        </div>
      </div>
     

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }

          .animate-fadeIn {
            animation: fadeIn 0.5s ease-out forwards;
          }

          .animate-slideDown {
            animation: slideDown 0.4s ease-out forwards;
          }

          .animate-scaleIn {
            animation: scaleIn 0.3s ease-out forwards;
          }

          .animate-pulse {
            animation: pulse 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }

          .stagger-1 {
            animation-delay: 0.1s;
          }

          .stagger-2 {
            animation-delay: 0.2s;
          }

          .stagger-3 {
            animation-delay: 0.3s;
          }

          .stagger-4 {
            animation-delay: 0.4s;
          }
        `}</style>

        {/* Search and Filters */}
        <div className={`flex items-center space-x-4 mb-8 ${isLoading ? 'opacity-0' : 'animate-fadeIn stagger-1'}`}>
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search cycles or processes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
            />
          </div>
          
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer transition-all duration-150"
            >
              <option>All Statuses</option>
              <option>Current</option>
              <option>Needs Review</option>
              <option>Outdated</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={riskFilter}
              onChange={(e) => setRiskFilter(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer transition-all duration-150"
            >
              <option>All Risk Levels</option>
              <option>High Risk</option>
              <option>Moderate Risk</option>
              <option>Low Risk</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          </div>
        </div>

        {/* Cycles List */}
        <div className="space-y-6">
          {isLoading ? (
            // Loading skeletons
            [...Array(3)].map((_, index) => (
              <div
                key={index}
                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse`}
              >
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              </div>
            ))
          ) : isSearching ? (
            // Search animation
            <div className="flex justify-center py-12">
              <div className="animate-pulse flex space-x-4">
                <div className="rounded-full bg-blue-400 h-3 w-3"></div>
                <div className="rounded-full bg-blue-400 h-3 w-3 animation-delay-200"></div>
                <div className="rounded-full bg-blue-400 h-3 w-3 animation-delay-400"></div>
              </div>
            </div>
          ) : filteredCycles.length > 0 ? (
            filteredCycles.map((cycle, index) => (
              <div
                key={cycle.id}
                className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md animate-scaleIn`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Cycle Header */}
                <div className="px-6 py-5 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h2 className="text-xl font-semibold text-gray-900">{cycle.name}</h2>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${cycle.riskColor}`}>
                          {cycle.riskLevel}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3">{cycle.description}</p>
                      <p className="text-gray-500 text-sm">
                        {cycle.processCount} processes • Last updated {cycle.lastUpdated}
                      </p>
                    </div>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 hover:scale-105 transition-all duration-150 font-medium text-sm transform">
                      View Details
                    </button>
                  </div>
                </div>

                {/* Processes List */}
                <div className="divide-y divide-gray-200">
                  {cycle.processes.map((process, index) => (
                    <ProcessCard key={index} process={process} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
              <p className="text-gray-600">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SOXFlowcharts;